import React, { createContext, useContext, useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import AuthService from 'services/auth.services.js';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const history = useHistory();
  const location = useLocation();

  // Load user session on mount
  useEffect(() => {
    const initializeUser = async () => {
      try {
        // Check for verified user first
        const savedVerifiedUser = JSON.parse(localStorage.getItem(AuthService.verified_user));
        if (savedVerifiedUser) {
          setUser(savedVerifiedUser);
          setLoading(false);
          return;
        }

        // Check for unverified user
        const savedUnverifiedUser = JSON.parse(localStorage.getItem(AuthService.unverified_user));
        if (savedUnverifiedUser) {
          setUser(savedUnverifiedUser);
          history.replace('/auth/otp');
        }
      } catch (error) {
        console.error('Error loading user session:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeUser();
  }, [history]);

  // Redirect logic for authentication
  useEffect(() => {
    if (!loading) {
      const verifiedUser = JSON.parse(localStorage.getItem(AuthService.verified_user));
      const unverifiedUser = JSON.parse(localStorage.getItem(AuthService.unverified_user));

      // If user is fully verified, allow access to admin routes
      if (verifiedUser) {
        if (location.pathname.startsWith('/auth')) {
          history.replace('/admin');
        }
        return;
      }

      // If user is unverified, redirect to OTP
      if (unverifiedUser) {
        if (location.pathname !== '/auth/otp') {
          history.replace('/auth/otp');
        }
        return;
      }

      // If no user, redirect to sign-in
      if (!verifiedUser) {
        if (location.pathname !== '/auth/sign-in') {
          history.replace('/auth/sign-in');
        }
      }
    }
  }, [loading, history, location]);

  const signIn = async (loginData) => {
    try {
      const unverifiedUser = await AuthService.login(loginData);
      setUser(unverifiedUser);
      history.replace('/auth/otp');
    } catch (error) {
      console.error('Error during sign-in:', error);
      throw error;
    }
  };

  const sendOTP = async (otpData) => {
    try {
      const verifiedUser = await AuthService.validateOTP(otpData);
      setUser(verifiedUser);
      history.replace('/admin');
    } catch (error) {
      console.error('Error validating OTP:', error);
      throw error;
    }
  };

  const resendOTP = async () => {
    try {
      await AuthService.resendOTP();
    } catch (error) {
      console.error('Error resending OTP:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await AuthService.logout();
      setUser(null);
      history.replace('/auth/sign-in');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider value={{ user, signIn, sendOTP, resendOTP, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
